import React, { useState, useEffect, useMemo } from "react";
import { X, ChevronRight, ChevronLeft, Target, Calendar, TrendingUp, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { userStorage } from '../utils/userStorage';
const WIZARD_STORAGE_KEY = "burningteam_wizard_goals";

// Errechnet den Montag einer ISO-Woche
function getMondayOfWeek(year, weekNum) {
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - jan4Day + 1 + (weekNum - 1) * 7);
  return monday;
}

// Gibt den Monatskey (YYYY-MM) für eine Woche zurück (basierend auf Donnerstag = ISO-Standard)
function getMonthKeyOfWeek(year, weekNum) {
  const monday = getMondayOfWeek(year, weekNum);
  const thursday = new Date(monday);
  thursday.setDate(monday.getDate() + 3);
  return `${thursday.getFullYear()}-${String(thursday.getMonth() + 1).padStart(2, "0")}`;
}

// Gibt die ISO-Wochennummer für ein Datum zurück
function getISOWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// Anzahl Monate von heute bis Zieldatum (inkl. beider Enden, mind. 1)
function countMonthsTo(targetDateStr) {
  if (!targetDateStr) return 12;
  const today = new Date();
  const target = new Date(targetDateStr + "T12:00:00");
  const months = (target.getFullYear() - today.getFullYear()) * 12
    + (target.getMonth() - today.getMonth()) + 1;
  return Math.max(1, months);
}

// Anzahl Wochen von dieser Woche bis zur Zieldatum-Woche (inkl. beider Enden, mind. 1)
function countWeeksTo(targetDateStr) {
  if (!targetDateStr) return 52;
  const today = new Date();
  const target = new Date(targetDateStr + "T12:00:00");
  const startWeek = getISOWeekNumber(today);
  const endWeek = getISOWeekNumber(target);
  // Grobe Berechnung in Wochen
  const diffMs = target - today;
  const diffWeeks = Math.ceil(diffMs / (7 * 24 * 3600 * 1000)) + 1;
  return Math.max(1, diffWeeks);
}

const MONTHS_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember"];

const STEPS = ["intro", "year_form", "year_confirm", "month_form", "month_confirm", "week_form", "week_done"];

export default function ZielplanungsAssistentModal({ open, onClose, goalTitle, milestones = [], existingData }) {
  const [step, setStep] = useState("intro");
  const [yearNumber, setYearNumber] = useState("");
  const [yearText, setYearText] = useState("");
  const [monthNumber, setMonthNumber] = useState("");
  const [weekNumber, setWeekNumber] = useState("");

  // Zieldatum: Standard = Ende des aktuellen Jahres
  const defaultTargetDate = `${new Date().getFullYear()}-12-31`;
  const [targetDate, setTargetDate] = useState(defaultTargetDate);
  // Trackt ob ein Meilenstein-Chip gewählt wurde (null = freier Text)
  const [selectedMilestoneOrigText, setSelectedMilestoneOrigText] = useState(null);

  const today = new Date();
  const currentYear = today.getFullYear();
  const minDate = today.toISOString().split("T")[0];
  const validMilestones = useMemo(() => milestones.filter(m => m.t && m.t.trim()), [milestones]);

  // Beim Öffnen: immer von vorne beginnen (Step zurücksetzen)
  useEffect(() => {
    if (open) {
      setStep("intro");
      setYearNumber("");
      setYearText("");
      setMonthNumber("");
      setWeekNumber("");
      setTargetDate(defaultTargetDate);
      setSelectedMilestoneOrigText(null);
    }
  }, [open]); // eslint-disable-line

  // Existierende Daten vorabfüllen
  useEffect(() => {
    if (existingData) {
      setYearNumber(String(existingData.yearGoal?.number || ""));
      setYearText(existingData.yearGoal?.text || "");
      if (existingData.targetDate) setTargetDate(existingData.targetDate);
    }
  }, [existingData]);

  // Auto-Berechnung wenn Jahresanzahl ODER Zieldatum sich ändert
  useEffect(() => {
    const n = parseFloat(yearNumber);
    if (!isNaN(n) && n > 0) {
      const totalMonths = countMonthsTo(targetDate);
      const totalWeeks = countWeeksTo(targetDate);
      setMonthNumber(String(Math.ceil(n / totalMonths)));
      setWeekNumber(String(Math.ceil(n / totalWeeks)));
    }
  }, [yearNumber, targetDate]);

  const handleReset = () => {
    setStep("year_form");
    setYearNumber("");
    setYearText("");
    setMonthNumber("");
    setWeekNumber("");
    setTargetDate(defaultTargetDate);
    setSelectedMilestoneOrigText(null);
  };

  const handleSave = () => {
    const n = parseFloat(yearNumber) || 0;
    const target = new Date(targetDate + "T12:00:00");
    const totalMonths = countMonthsTo(targetDate);
    const totalWeeks = countWeeksTo(targetDate);
    const mn = parseFloat(monthNumber) || Math.ceil(n / totalMonths);
    const wn = parseFloat(weekNumber) || Math.ceil(n / totalWeeks);

    // Monats-Ziele: von aktuellem Monat bis Zieldatum
    const monthGoals = {};
    for (let i = 0; i < totalMonths; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      monthGoals[key] = { number: mn, text: yearText, done: false, progress: 0 };
    }

    // Wochen-Ziele: von aktueller Woche bis Zieldatum-Woche
    const weekGoals = {};
    let weekYear = today.getFullYear();
    let weekNum = getISOWeekNumber(today);
    for (let i = 0; i < totalWeeks; i++) {
      const key = `${weekYear}-W${weekNum}`;
      const monthKey = getMonthKeyOfWeek(weekYear, weekNum);
      weekGoals[key] = { number: wn, text: yearText, done: false, progress: 0, monthKey };
      weekNum++;
      const testDate = getMondayOfWeek(weekYear, weekNum);
      if (testDate.getFullYear() > weekYear) {
        weekYear = testDate.getFullYear();
        weekNum = getISOWeekNumber(testDate);
      }
    }

    const wizardData = {
      createdAt: new Date().toISOString(),
      year: currentYear,
      targetDate,
      yearGoal: { number: n, text: yearText, done: false },
      monthGoals,
      weekGoals,
      // Wizard-Meilenstein: Text = "[Anzahl] [Beschreibung]", Original = gewählter Chip-Text (null = freier Text)
      wizardMilestoneText: (n > 0 && yearText.trim()) ? `${n} ${yearText.trim()}` : null,
      wizardMilestoneOrigText: selectedMilestoneOrigText,
    };

    userStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(wizardData));

    // Jahresziel in Kalender schreiben (auch für nächstes Jahr falls nötig)
    const goalStr = `${n} ${yearText}`;
    const yearGoals = JSON.parse(userStorage.getItem("zielplanung_year_goals") || "{}");
    yearGoals[currentYear] = goalStr;
    if (target.getFullYear() > currentYear) {
      yearGoals[target.getFullYear()] = goalStr;
    }
    userStorage.setItem("zielplanung_year_goals", JSON.stringify(yearGoals));

    // Monatsziele in Kalender schreiben
    const mGoals = JSON.parse(userStorage.getItem("zielplanung_month_goals") || "{}");
    Object.keys(monthGoals).forEach(key => { mGoals[key] = `${mn} ${yearText}`; });
    userStorage.setItem("zielplanung_month_goals", JSON.stringify(mGoals));

    // Wochenziele in Kalender schreiben
    const wGoals = JSON.parse(userStorage.getItem("zielplanung_week_goals") || "{}");
    Object.keys(weekGoals).forEach(key => { wGoals[key] = `${wn} ${yearText}`; });
    userStorage.setItem("zielplanung_week_goals", JSON.stringify(wGoals));

    window.dispatchEvent(new CustomEvent("burningteam-wizard-sync"));
    onClose(true);
  };

  const handleClose = () => onClose(false);
  if (!open) return null;

  const stepIndex = STEPS.indexOf(step);
  const progressPct = Math.round((stepIndex / (STEPS.length - 1)) * 100);
  const yearFormValid = yearNumber.trim() && yearText.trim() && targetDate;

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-t-2xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Target size={20} />
            <span className="font-bold text-lg">Zielplanungs-Assistent</span>
          </div>
          <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Progress bar */}
        {step !== "intro" && (
          <div className="px-4 pt-3">
            <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-500 rounded-full"
                style={{ width: `${progressPct}%` }} />
            </div>
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>Jahr</span><span>Monat</span><span>Woche</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {step === "intro" && <StepIntro goalTitle={goalTitle} />}
          {step === "year_form" && (
            <StepYearForm
              yearNumber={yearNumber} setYearNumber={setYearNumber}
              yearText={yearText} setYearText={setYearText}
              onMilestoneSelect={(origText) => setSelectedMilestoneOrigText(origText)}
              onTextChange={() => setSelectedMilestoneOrigText(null)}
              targetDate={targetDate} setTargetDate={setTargetDate}
              minDate={minDate}
              milestones={validMilestones} goalTitle={goalTitle} />
          )}
          {step === "year_confirm" && (
            <StepYearConfirm yearNumber={yearNumber} yearText={yearText}
              year={currentYear} targetDate={targetDate} />
          )}
          {step === "month_form" && (
            <StepMonthForm
              yearNumber={yearNumber} yearText={yearText}
              monthNumber={monthNumber} setMonthNumber={setMonthNumber}
              targetDate={targetDate} />
          )}
          {step === "month_confirm" && (
            <StepMonthConfirm monthNumber={monthNumber} yearText={yearText} targetDate={targetDate} />
          )}
          {step === "week_form" && (
            <StepWeekForm
              yearNumber={yearNumber} yearText={yearText}
              weekNumber={weekNumber} setWeekNumber={setWeekNumber}
              targetDate={targetDate} />
          )}
          {step === "week_done" && (
            <StepWeekDone weekNumber={weekNumber} monthNumber={monthNumber}
              yearNumber={yearNumber} yearText={yearText} targetDate={targetDate} />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100">
          {step === "intro" && (
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleClose} className="flex-1">Nein, danke</Button>
              <Button onClick={() => setStep("year_form")}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                Ja, jetzt planen!
              </Button>
            </div>
          )}
          {["year_form", "year_confirm", "month_form", "month_confirm", "week_form"].includes(step) && (
            <div className="flex gap-3">
              <Button variant="outline" size="sm" onClick={handleClose} className="text-slate-500">Abbrechen</Button>
              {stepIndex > 1 && (
                <Button variant="outline" size="sm" onClick={() => setStep(STEPS[stepIndex - 1])}
                  className="flex items-center gap-1">
                  <ChevronLeft size={14} /> Zurück
                </Button>
              )}
              <Button
                disabled={step === "year_form" && !yearFormValid}
                onClick={() => {
                  if (step === "year_form" && !yearFormValid) return;
                  setStep(STEPS[stepIndex + 1]);
                }}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white flex items-center justify-center gap-1">
                Weiter <ChevronRight size={14} />
              </Button>
            </div>
          )}
          {step === "week_done" && (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleClose} className="flex-1 text-slate-500">Abbrechen</Button>
                <Button variant="outline" size="sm" onClick={handleReset}
                  className="flex-1 flex items-center justify-center gap-1 text-emerald-600 border-emerald-200">
                  <RotateCcw size={13} /> Neu planen
                </Button>
              </div>
              <Button onClick={handleSave}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold">
                Speichern & Abschließen
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Sub-Komponenten ----

function StepIntro({ goalTitle }) {
  return (
    <div className="text-center space-y-4 py-2">
      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
        <Target size={32} className="text-emerald-600" />
      </div>
      <h2 className="text-xl font-bold text-slate-800">Dein Ziel wurde gespeichert!</h2>
      {goalTitle && <p className="text-emerald-700 font-semibold text-lg">"{goalTitle}"</p>}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-left">
        <p className="text-sm text-slate-700 leading-relaxed">
          Wusstest du, dass Menschen <strong>42% häufiger</strong> ihre Ziele erreichen,
          wenn sie diese konkret in <strong>Jahres-, Monats- und Wochenpläne</strong> aufteilen?
        </p>
        <p className="text-sm text-slate-700 mt-2 leading-relaxed">
          Der Assistent führt dich in 3 einfachen Schritten durch deine Zielplanung.
        </p>
      </div>
      <p className="text-base font-semibold text-slate-800">Möchtest du jetzt planen?</p>
    </div>
  );
}

function StepYearForm({ yearNumber, setYearNumber, yearText, setYearText, onMilestoneSelect, onTextChange, targetDate, setTargetDate, minDate, milestones, goalTitle }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-emerald-700 font-bold text-lg">
        <Calendar size={20} /> Jahresplanung
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">
        Was musst du konkret schaffen, um dein Ziel zu erreichen?
        Gib die Zahl, die Beschreibung und dein Zieldatum ein.
      </p>

      {milestones.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <p className="text-xs font-semibold text-amber-700 mb-2">Ideen aus deinen Meilensteinen:</p>
          <div className="flex flex-wrap gap-1">
            {milestones.map((m, i) => (
              <button key={i} type="button"
                onClick={() => { setYearText(m.t); if (onMilestoneSelect) onMilestoneSelect(m.t); }}
                className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-800 px-2 py-1 rounded-full border border-amber-300 transition-colors">
                {m.t}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="text-sm font-semibold text-slate-700">Anzahl (Zahl)</label>
          <Input type="number" value={yearNumber} onChange={e => setYearNumber(e.target.value)}
            placeholder="z.B. 50" min="1"
            className="mt-1 border-emerald-300 focus:ring-emerald-500 text-lg font-semibold" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700">Was? (Beschreibung)</label>
          <Input value={yearText} onChange={e => { setYearText(e.target.value); if (onTextChange) onTextChange(); }}
            placeholder="z.B. Teampartner einschreiben"
            className="mt-1 border-emerald-300 focus:ring-emerald-500" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700">Bis wann?</label>
          <input type="date" value={targetDate} min={minDate}
            onChange={e => setTargetDate(e.target.value)}
            className="mt-1 w-full border border-emerald-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
      </div>

      {yearNumber && yearText && targetDate && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
          <p className="text-sm text-slate-600">Mein Ziel:</p>
          <p className="text-xl font-bold text-emerald-700">{yearNumber} {yearText}</p>
          <p className="text-xs text-emerald-600 mt-1">bis {new Date(targetDate + "T12:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}</p>
        </div>
      )}
    </div>
  );
}

function StepYearConfirm({ yearNumber, yearText, year, targetDate }) {
  const target = targetDate ? new Date(targetDate + "T12:00:00") : null;
  const isNextYear = target && target.getFullYear() > year;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-emerald-700 font-bold text-lg">
        <Target size={20} /> Jahresziel gespeichert
      </div>

      <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 text-center">
        <p className="text-xs text-emerald-600 uppercase tracking-wide mb-1">Dein Ziel</p>
        <p className="text-2xl font-bold text-emerald-700">{yearNumber} {yearText}</p>
        {target && (
          <p className="text-xs text-emerald-600 mt-1">
            bis {target.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}
          </p>
        )}
      </div>

      {isNextYear && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <Calendar size={18} className="text-amber-500 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-700">
            Dein Zieldatum liegt im Jahr <strong>{target.getFullYear()}</strong> – das Jahresziel wird
            auch dort eingetragen.
          </p>
        </div>
      )}

      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
        <Calendar size={18} className="text-blue-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-800">Im Kalender sichtbar</p>
          <p className="text-xs text-blue-700 mt-0.5">
            Dein Jahresziel erscheint jetzt im Zielplanungskalender unter <strong>Jahresziel</strong>.
          </p>
        </div>
      </div>

      <div className="bg-slate-50 rounded-xl p-3">
        <p className="text-sm text-slate-700 leading-relaxed italic">
          "Ein Ziel ist ein Traum mit einem Datum." Jetzt planen wir, wie du es Monat für Monat erreichst.
        </p>
      </div>
    </div>
  );
}

function StepMonthForm({ yearNumber, yearText, monthNumber, setMonthNumber, targetDate }) {
  const totalMonths = countMonthsTo(targetDate);
  const calculated = yearNumber ? Math.ceil(parseFloat(yearNumber) / totalMonths) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-blue-700 font-bold text-lg">
        <Calendar size={20} /> Monatsplanung
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
        <p className="text-xs text-blue-600 mb-1">
          Berechnung: {yearNumber} ÷ {totalMonths} Monate (bis Zieldatum) = aufrunden
        </p>
        <p className="text-3xl font-bold text-blue-700">{calculated}</p>
        <p className="text-sm text-blue-600 mt-1">pro Monat · {yearText}</p>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed">
        Das ist dein monatliches Ziel. Du kannst die Zahl unten anpassen, falls nötig.
      </p>

      <div>
        <label className="text-sm font-semibold text-slate-700">Anzahl pro Monat</label>
        <Input type="number" value={monthNumber} onChange={e => setMonthNumber(e.target.value)}
          placeholder={String(calculated)} min="0" step="0.5"
          className="mt-1 border-blue-300 focus:ring-blue-500 text-lg font-semibold" />
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-2 text-center">
        <p className="text-sm text-slate-600">
          Monatliches Ziel: <strong>{monthNumber || calculated} {yearText}</strong>
        </p>
      </div>
    </div>
  );
}

function StepMonthConfirm({ monthNumber, yearText, targetDate }) {
  const today = new Date();
  const totalMonths = countMonthsTo(targetDate);
  const months = Array.from({ length: totalMonths }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    return { name: MONTHS_DE[d.getMonth()], year: d.getFullYear(), current: today.getFullYear() };
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-blue-700 font-bold text-lg">
        <TrendingUp size={20} /> Monatsziele eingetragen
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
        <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Monatliches Ziel</p>
        <p className="text-xl font-bold text-blue-700">{monthNumber} {yearText}</p>
      </div>

      <div className="grid grid-cols-3 gap-1.5 max-h-44 overflow-y-auto">
        {months.map((m, i) => (
          <div key={i} className="bg-white border border-blue-200 rounded-lg p-2 text-center">
            <p className="text-xs text-blue-600 font-medium">{m.name}</p>
            {m.year !== m.current && <p className="text-[10px] text-slate-400">{m.year}</p>}
            <p className="text-sm font-bold text-blue-800">{monthNumber}</p>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
        <Calendar size={18} className="text-emerald-500 mt-0.5 shrink-0" />
        <p className="text-xs text-emerald-700">
          Diese <strong>{totalMonths} Monate</strong> erscheinen jetzt im Kalender unter <strong>Monatsziel</strong>.
        </p>
      </div>
    </div>
  );
}

function StepWeekForm({ yearNumber, yearText, weekNumber, setWeekNumber, targetDate }) {
  const totalWeeks = countWeeksTo(targetDate);
  const calculated = yearNumber ? Math.ceil(parseFloat(yearNumber) / totalWeeks) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-purple-700 font-bold text-lg">
        <TrendingUp size={20} /> Wochenplanung
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
        <p className="text-xs text-purple-600 mb-1">
          Berechnung: {yearNumber} ÷ {totalWeeks} Wochen (bis Zieldatum) = aufrunden
        </p>
        <p className="text-3xl font-bold text-purple-700">{calculated}</p>
        <p className="text-sm text-purple-600 mt-1">pro Woche · {yearText}</p>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed">
        Das ist dein wöchentliches Ziel. Kleine Schritte – große Wirkung!
      </p>

      <div>
        <label className="text-sm font-semibold text-slate-700">Anzahl pro Woche</label>
        <Input type="number" value={weekNumber} onChange={e => setWeekNumber(e.target.value)}
          placeholder={String(calculated)} min="0" step="0.5"
          className="mt-1 border-purple-300 focus:ring-purple-500 text-lg font-semibold" />
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-xl p-2 text-center">
        <p className="text-sm text-slate-600">
          Wöchentliches Ziel: <strong>{weekNumber || calculated} {yearText}</strong>
        </p>
      </div>
    </div>
  );
}

function StepWeekDone({ weekNumber, monthNumber, yearNumber, yearText, targetDate }) {
  const target = targetDate ? new Date(targetDate + "T12:00:00") : null;
  const totalWeeks = countWeeksTo(targetDate);
  const totalMonths = countMonthsTo(targetDate);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-purple-700 font-bold text-lg">
        <Target size={20} /> Planung abgeschlossen!
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
        <p className="text-xs text-purple-600 uppercase tracking-wide mb-1">Wöchentliches Ziel</p>
        <p className="text-xl font-bold text-purple-700">{weekNumber} {yearText}</p>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
        <p className="text-xs font-semibold text-slate-500 uppercase">Deine Zielkette</p>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-2 h-2 bg-purple-400 rounded-full" />
          <span className="text-slate-600">Jede Woche ({totalWeeks} Wochen): <strong>{weekNumber} {yearText}</strong></span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-2 h-2 bg-blue-400 rounded-full" />
          <span className="text-slate-600">Jeden Monat ({totalMonths} Monate): <strong>{monthNumber} {yearText}</strong></span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-2 h-2 bg-emerald-400 rounded-full" />
          <span className="text-slate-600">Gesamt: <strong>{yearNumber} {yearText}</strong></span>
        </div>
        {target && (
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 bg-amber-400 rounded-full" />
            <span className="text-slate-600">Zieldatum: <strong>{target.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}</strong></span>
          </div>
        )}
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
        <p className="text-sm text-slate-700 leading-relaxed">
          Im Kalender kannst du jetzt <strong>jede Woche abhaken</strong>. Wenn du alle Wochen
          eines Monats erledigt hast, wird der Monat automatisch abgehakt! 🎯
        </p>
      </div>
    </div>
  );
}

export { WIZARD_STORAGE_KEY };
